import { Controller } from "@hotwired/stimulus"

// The event drawer: one Turbo Frame (#drawer) any link with data-turbo-frame="drawer" opens into. The
// page URL carries ?edit=<listing> so the drawer can be linked and refreshed. J/K move through the
// listings linked on the page, Escape closes, ⌘/Ctrl+Enter saves. After something is saved in the
// drawer the page behind it is refreshed in place (a morph), so its rows show the change.
export default class extends Controller {
  static targets = ["frame"]

  connect() {
    this.closeBeforeCache = () => this.refreshing || this.close()
    document.addEventListener("turbo:before-cache", this.closeBeforeCache)
    if (this.frameTarget.querySelector("[data-drawer-event]")) this.loaded()
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.closeBeforeCache)
    this.setOpen(false)
  }

  loaded() {
    const panel = this.panel()
    if (!panel) return

    this.setOpen(true)
    this.setUrlParam(panel.dataset.drawerEvent)
    this.markCurrent(panel.dataset.drawerEvent)
    this.updatePosition(panel)
    if (this.refreshPending) {
      this.refreshPending = false
      this.refreshPage()
    }
  }

  submitEnd(event) {
    if (event.detail.success && this.frameTarget.contains(event.target)) this.refreshPending = true
  }

  close() {
    if (!this.isOpen()) return

    this.frameTarget.removeAttribute("src")
    this.frameTarget.innerHTML = ""
    this.setOpen(false)
    this.setUrlParam(null)
    this.markCurrent(null)
  }

  backdrop(event) {
    if (event.target === event.currentTarget) this.close()
  }

  next() {
    this.move(1)
  }

  previous() {
    this.move(-1)
  }

  key(event) {
    if (!this.isOpen() || event.altKey) return

    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
      const form = this.frameTarget.querySelector("form[data-drawer-form]")
      if (form) {
        event.preventDefault()
        form.requestSubmit()
      }
      return
    }
    if (event.metaKey || event.ctrlKey) return
    if (event.key === "Escape") {
      event.preventDefault()
      return this.close()
    }
    if (this.typing(event.target)) return

    const key = event.key.toLowerCase()
    if (key === "j" || key === "k") {
      event.preventDefault()
      this.move(key === "j" ? 1 : -1)
    }
  }

  // Private

  panel() {
    return this.frameTarget.querySelector("[data-drawer-event]")
  }

  isOpen() {
    return document.documentElement.hasAttribute("data-drawer-open")
  }

  setOpen(open) {
    document.documentElement.toggleAttribute("data-drawer-open", open)
  }

  // The listings on the page, in the order they're shown, once each
  listings() {
    const seen = new Set()
    return [ ...document.querySelectorAll("a[data-turbo-frame=drawer][data-drawer-item]") ].filter((link) => {
      if (this.element.contains(link) || seen.has(link.dataset.drawerItem)) return false
      seen.add(link.dataset.drawerItem)
      return true
    })
  }

  move(step) {
    const panel = this.panel()
    const links = this.listings()
    const index = links.findIndex((link) => link.dataset.drawerItem === panel?.dataset.drawerEvent)
    const target = links[index + step]
    if (target) this.frameTarget.src = target.href
  }

  updatePosition(panel) {
    const links = this.listings()
    const index = links.findIndex((link) => link.dataset.drawerItem === panel.dataset.drawerEvent)
    const position = panel.querySelector("[data-drawer-position]")
    if (position) position.textContent = index >= 0 ? `${index + 1} of ${links.length}` : ""
    panel.querySelectorAll("[data-drawer-step]").forEach((button) => {
      const step = Number(button.dataset.drawerStep)
      button.disabled = index < 0 || !links[index + step]
    })
  }

  markCurrent(key) {
    document.querySelectorAll("[data-drawer-current]").forEach((row) => row.removeAttribute("data-drawer-current"))
    if (!key) return

    this.listings().forEach((link) => {
      if (link.dataset.drawerItem === key) link.closest("tr, [data-drawer-row]")?.setAttribute("data-drawer-current", "")
    })
  }

  setUrlParam(value) {
    const url = new URL(window.location.href)
    if (value) url.searchParams.set("edit", value)
    else url.searchParams.delete("edit")
    if (url.href !== window.location.href) window.history.replaceState(window.history.state, "", url.href)
  }

  // Redraws the page behind the drawer without touching the drawer itself
  refreshPage() {
    if (!window.Turbo) return

    this.refreshing = true
    this.frameTarget.setAttribute("data-turbo-permanent", "")
    const release = () => {
      this.refreshing = false
      this.frameTarget.removeAttribute("data-turbo-permanent")
      this.markCurrent(this.panel()?.dataset.drawerEvent)
      const panel = this.panel()
      if (panel) this.updatePosition(panel)
      document.removeEventListener("turbo:load", release)
    }
    document.addEventListener("turbo:load", release)
    window.Turbo.visit(window.location.href, { action: "replace" })
  }

  typing(element) {
    if (!element || element.isContentEditable) return false
    if (element.tagName === "INPUT") return !["checkbox", "radio", "button", "submit"].includes(element.type)
    return [ "TEXTAREA", "SELECT" ].includes(element.tagName)
  }
}
